import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { CancelTokenSource } from "axios";

import SelectOption from "src/app/components/SelectOption/SelectOption";
import SearchSelectDropdown from "src/app/dropdowns/SearchSelectDropdown/SearchSelectDropdown";
import { translateMessage } from "src/app/methods/translateMessage";
import {
  GlobalTaskSearchResult,
  ReactSelectOptionWithReactNodeLabel,
} from "src/types";
import { RootState } from "src/redux/reducers";
import { SearchResultsService } from "src/services";
import useGetGlobalTasksSearchResults from "src/hooks/useGetGlobalTasksSearchResults";
import creatorPlaceholderIcon from "src/images/creator-placeholder.svg";

interface Props {
  col: any;
  sendFilter: any;
  filters: any;
}

export default function GlobalTasksSelectFilter({
  col,
  sendFilter,
  filters,
}: Props) {
  const [selectedOptions, setSelectedOptions] = useState<
    ReactSelectOptionWithReactNodeLabel[] | null
  >(null);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const intl = useIntl();

  const [
    globalTasksSearchResultsState,
    initializeGlobalTasksSearchResultsFetch,
  ] = useGetGlobalTasksSearchResults();

  const generateOptions = (
    options: GlobalTaskSearchResult[],
  ): ReactSelectOptionWithReactNodeLabel[] => {
    return options.map((globalTask) => ({
      value: globalTask.uuid,
      label: (
        <SelectOption
          name={globalTask.title}
          imageSource={globalTask.avatarUrl}
          fallbackImageSource={creatorPlaceholderIcon}
        />
      ),
    }));
  };

  const filterValue: string[] | undefined = useMemo(() => {
    return filters[col.uuid]?.value;
  }, [filters, col.uuid]);

  const onChange = (newOptions: ReactSelectOptionWithReactNodeLabel[]) => {
    setSelectedOptions(newOptions);

    const updatedFilterValues = newOptions.map((o) => o.value);

    sendFilter(col.uuid, updatedFilterValues);
  };

  const handleLoadOptions = async (
    inputValue: string,
    cancelToken: CancelTokenSource,
  ) => {
    const data = await SearchResultsService.getGlobalTasks(
      activeWorkspaceUuid,
      [inputValue],
      cancelToken,
    );
    return generateOptions(data);
  };

  useEffect(() => {
    if (!filterValue) return;

    if (filterValue.length > 0) {
      initializeGlobalTasksSearchResultsFetch(activeWorkspaceUuid, filterValue);
    }
  }, [filterValue, activeWorkspaceUuid]);

  useEffect(() => {
    if (globalTasksSearchResultsState.type === "done") {
      setSelectedOptions(generateOptions(globalTasksSearchResultsState.data));
    }
  }, [globalTasksSearchResultsState]);

  useEffect(() => {
    if (!filterValue) return;

    if (filterValue.length === 0) {
      setSelectedOptions([]);
    }
  }, [filterValue]);

  return (
    <SearchSelectDropdown<ReactSelectOptionWithReactNodeLabel>
      value={selectedOptions}
      handleChange={onChange}
      placeholder={translateMessage({
        intl,
        id: "ws_search_global_creator",
        defaultMessage: "Type to find a global creator",
      })}
      fetchOptions={handleLoadOptions}
      isMulti
      withTiles
      openMenuOnFocus={false}
    />
  );
}
